import React, { useState, useEffect } from 'react';
import {
  Building2,
  MapPin,
  Plus,
  Copy,
  Check,
  Edit2,
  Trash2,
  X,
  ExternalLink,
  Search,
  MessageSquare,
  Phone,
  Mail,
  Loader2,
  Globe,
  Code,
  Calendar,
  FileText,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Monitor
} from 'lucide-react';
import api from '../services/api';

export default function FranquiciadosManager() {
  const [franquiciados, setFranquiciados] = useState([]);
  const [lugares, setLugares] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('franquiciados'); // 'franquiciados', 'lugares' o 'clientes'
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('franquiciado'); // 'franquiciado', 'lugar' o 'cliente'
  const [editingItem, setEditingItem] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [search, setSearch] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    taquilla_id: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    city: '',
    address: '',
    type: 'otro',
    franquiciado_id: '',
    website_url: '',
    widget_position: 'bottom-right',
    // Campos de contrato
    contract_start_date: '',
    contract_end_date: '',
    accounts: [] // Lista de cuentas/aparatos asociados
  });

  // Estado para expandir franquiciados
  const [expandedFranquiciados, setExpandedFranquiciados] = useState({});
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [selectedFranquiciado, setSelectedFranquiciado] = useState(null);
  const [accountFormData, setAccountFormData] = useState({
    name: '',
    taquilla_id: '',
    city: '',
    contract_start_date: '',
    contract_end_date: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [franqRes, lugaresRes, clientesRes] = await Promise.all([
        api.get('/api/franquiciados'),
        api.get('/api/lugares'),
        api.get('/api/clientes').catch(() => ({ data: { clientes: [] } }))
      ]);
      setFranquiciados(franqRes.data.franquiciados || []);
      setLugares(lugaresRes.data.lugares || []);
      setClientes(clientesRes.data.clientes || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(id);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const openCreateModal = (type) => {
    setModalType(type);
    setEditingItem(null);
    setFormData({
      name: '',
      taquilla_id: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      city: '',
      address: '',
      type: 'otro',
      franquiciado_id: '',
      website_url: '',
      widget_position: 'bottom-right',
      contract_start_date: '',
      contract_end_date: '',
      accounts: []
    });
    setShowModal(true);
  };

  const openEditModal = (item, type) => {
    setModalType(type);
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      taquilla_id: item.taquilla_id || '',
      contact_name: item.contact_name || '',
      contact_email: item.contact_email || '',
      contact_phone: item.contact_phone || '',
      city: item.city || '',
      address: item.address || '',
      type: item.type || 'otro',
      franquiciado_id: item.franquiciado_id || '',
      website_url: item.website_url || '',
      widget_position: item.widget_position || 'bottom-right',
      contract_start_date: item.contract_start_date || '',
      contract_end_date: item.contract_end_date || '',
      accounts: item.accounts || []
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalType === 'franquiciado') {
        if (editingItem) {
          await api.put(`/api/franquiciados/${editingItem.id}`, formData);
        } else {
          await api.post('/api/franquiciados', formData);
        }
      } else if (modalType === 'lugar') {
        if (editingItem) {
          await api.put(`/api/lugares/${editingItem.id}`, formData);
        } else {
          await api.post('/api/lugares', formData);
        }
      } else if (modalType === 'cliente') {
        if (editingItem) {
          await api.put(`/api/clientes/${editingItem.id}`, formData);
        } else {
          await api.post('/api/clientes', formData);
        }
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving:', error);
      alert(error.response?.data?.error || 'Error al guardar');
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try {
      if (type === 'franquiciado') {
        await api.delete(`/api/franquiciados/${id}`);
      } else if (type === 'lugar') {
        await api.delete(`/api/lugares/${id}`);
      } else if (type === 'cliente') {
        await api.delete(`/api/clientes/${id}`);
      }
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const toggleExpandFranquiciado = (id) => {
    setExpandedFranquiciados(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const openAccountModal = (franquiciado) => {
    setSelectedFranquiciado(franquiciado);
    setAccountFormData({
      name: '',
      taquilla_id: '',
      city: '',
      contract_start_date: '',
      contract_end_date: ''
    });
    setShowAccountModal(true);
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!selectedFranquiciado) return;

    try {
      // Añadir cuenta al franquiciado
      const updatedAccounts = [...(selectedFranquiciado.accounts || []), {
        id: `acc-${Date.now()}`,
        ...accountFormData,
        created_at: new Date().toISOString()
      }];

      await api.put(`/api/franquiciados/${selectedFranquiciado.id}`, {
        accounts: updatedAccounts
      });

      setShowAccountModal(false);
      loadData();
    } catch (error) {
      console.error('Error adding account:', error);
      alert('Error al añadir la cuenta');
    }
  };

  const handleDeleteAccount = async (franquiciado, accountId) => {
    if (!window.confirm('¿Eliminar esta cuenta?')) return;
    try {
      const updatedAccounts = (franquiciado.accounts || []).filter(a => a.id !== accountId);
      await api.put(`/api/franquiciados/${franquiciado.id}`, {
        accounts: updatedAccounts
      });
      loadData();
    } catch (error) {
      console.error('Error deleting account:', error);
    }
  };

  // Calcular días restantes de contrato
  const getDaysUntilExpiry = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Clase de estado del contrato
  const getContractStatusClass = (daysLeft) => {
    if (daysLeft === null) return 'bg-gray-100 text-gray-600';
    if (daysLeft < 0) return 'bg-red-100 text-red-700';
    if (daysLeft <= 30) return 'bg-orange-100 text-orange-700';
    if (daysLeft <= 90) return 'bg-yellow-100 text-yellow-700';
    return 'bg-green-100 text-green-700';
  };

  const filteredFranquiciados = franquiciados.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase()) ||
    f.city?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredLugares = lugares.filter(l =>
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.taquilla_id?.toLowerCase().includes(search.toLowerCase()) ||
    l.city?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredClientes = clientes.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.website_url?.toLowerCase().includes(search.toLowerCase())
  );

  const lugarTypes = [
    { value: 'museo', label: 'Museo' },
    { value: 'cine', label: 'Cine' },
    { value: 'hotel', label: 'Hotel' },
    { value: 'teatro', label: 'Teatro' },
    { value: 'parque', label: 'Parque' },
    { value: 'monumento', label: 'Monumento' },
    { value: 'evento', label: 'Evento' },
    { value: 'otro', label: 'Otro' }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <Globe className="w-7 h-7 mr-3 text-blue-600" />
            Chats Externos
          </h2>
          <p className="text-gray-500 mt-1">URLs de chat para franquiciados, lugares y clientes finales</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => setActiveTab('franquiciados')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            activeTab === 'franquiciados'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Building2 className="w-4 h-4 inline mr-2" />
          Franquiciados ({franquiciados.length})
        </button>
        <button
          onClick={() => setActiveTab('lugares')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            activeTab === 'lugares'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <MapPin className="w-4 h-4 inline mr-2" />
          Lugares ({lugares.length})
        </button>
        <button
          onClick={() => setActiveTab('clientes')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            activeTab === 'clientes'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Code className="w-4 h-4 inline mr-2" />
          Clientes finales ({clientes.length})
        </button>
      </div>

      {/* Search and Add */}
      <div className="flex items-center justify-between mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
        <button
          onClick={() => openCreateModal(activeTab === 'franquiciados' ? 'franquiciado' : activeTab === 'lugares' ? 'lugar' : 'cliente')}
          className={`flex items-center px-4 py-2 text-white rounded-lg transition ${
            activeTab === 'franquiciados' ? 'bg-blue-600 hover:bg-blue-700' :
            activeTab === 'lugares' ? 'bg-green-600 hover:bg-green-700' :
            'bg-purple-600 hover:bg-purple-700'
          }`}
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuevo {activeTab === 'franquiciados' ? 'Franquiciado' : activeTab === 'lugares' ? 'Lugar' : 'Cliente'}
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          {activeTab === 'clientes' ? (
            // Clientes List
            <div className="divide-y">
              {filteredClientes.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Code className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay clientes finales registrados</p>
                  <p className="text-sm mt-2">Crea uno para generar un widget de chat embebido</p>
                </div>
              ) : (
                filteredClientes.map(cliente => (
                  <div key={cliente.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h3 className="font-semibold text-gray-800">{cliente.name}</h3>
                          {cliente.is_active && (
                            <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                              Activo
                            </span>
                          )}
                        </div>
                        {cliente.website_url && (
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <Globe className="w-4 h-4 mr-1" />
                            <a href={cliente.website_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                              {cliente.website_url}
                            </a>
                          </div>
                        )}
                        {/* URL del chat embebido */}
                        <div className="flex items-center mt-2 bg-gray-100 rounded-lg p-2">
                          <MessageSquare className="w-4 h-4 text-purple-500 mr-2" />
                          <code className="text-sm text-gray-600 flex-1">{cliente.chat_url}</code>
                          <button
                            onClick={() => handleCopyUrl(cliente.chat_url, cliente.id)}
                            className="ml-2 p-1 hover:bg-gray-200 rounded"
                            title="Copiar URL"
                          >
                            {copiedUrl === cliente.id ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-500" />
                            )}
                          </button>
                          <a
                            href={cliente.chat_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1 p-1 hover:bg-gray-200 rounded"
                            title="Abrir chat"
                          >
                            <ExternalLink className="w-4 h-4 text-gray-500" />
                          </a>
                        </div>
                        {/* Código del widget */}
                        {cliente.widget_code && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">Codigo para embeber:</p>
                            <div className="flex items-center bg-gray-800 rounded-lg p-2">
                              <code className="text-xs text-green-400 flex-1 truncate">{cliente.widget_code}</code>
                              <button
                                onClick={() => handleCopyUrl(cliente.widget_code, `widget-${cliente.id}`)}
                                className="ml-2 p-1 hover:bg-gray-700 rounded"
                                title="Copiar código"
                              >
                                {copiedUrl === `widget-${cliente.id}` ? (
                                  <Check className="w-4 h-4 text-green-400" />
                                ) : (
                                  <Copy className="w-4 h-4 text-gray-400" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => openEditModal(cliente, 'cliente')}
                          className="p-2 hover:bg-purple-50 rounded text-purple-600"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(cliente.id, 'cliente')}
                          className="p-2 hover:bg-red-50 rounded text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : activeTab === 'franquiciados' ? (
            // Franquiciados List
            <div className="divide-y">
              {filteredFranquiciados.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay franquiciados registrados</p>
                </div>
              ) : (
                filteredFranquiciados.map(franq => {
                  const isExpanded = expandedFranquiciados[franq.id];
                  const accountsCount = franq.accounts?.length || 0;

                  return (
                    <div key={franq.id} className="hover:bg-gray-50">
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <button
                                onClick={() => toggleExpandFranquiciado(franq.id)}
                                className="mr-2 p-1 hover:bg-gray-200 rounded"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-gray-500" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-gray-500" />
                                )}
                              </button>
                              <h3 className="font-semibold text-gray-800">{franq.name}</h3>
                              {franq.is_active && (
                                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                  Activo
                                </span>
                              )}
                              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                {accountsCount} {accountsCount === 1 ? 'cuenta' : 'cuentas'}
                              </span>
                            </div>
                            <div className="flex items-center text-sm text-gray-500 mt-1 space-x-4 ml-7">
                              {franq.city && (
                                <span className="flex items-center">
                                  <MapPin className="w-4 h-4 mr-1" />
                                  {franq.city}
                                </span>
                              )}
                              {franq.contact_name && (
                                <span>{franq.contact_name}</span>
                              )}
                              {franq.contact_phone && (
                                <span className="flex items-center">
                                  <Phone className="w-4 h-4 mr-1" />
                                  {franq.contact_phone}
                                </span>
                              )}
                            </div>
                            {/* URL del chat */}
                            <div className="flex items-center mt-2 ml-7 bg-gray-100 rounded-lg p-2">
                              <MessageSquare className="w-4 h-4 text-blue-500 mr-2" />
                              <code className="text-sm text-gray-600 flex-1">{franq.chat_url}</code>
                              <button
                                onClick={() => handleCopyUrl(franq.chat_url, franq.id)}
                                className="ml-2 p-1 hover:bg-gray-200 rounded"
                                title="Copiar URL"
                              >
                                {copiedUrl === franq.id ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <Copy className="w-4 h-4 text-gray-500" />
                                )}
                              </button>
                              <a
                                href={franq.chat_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-1 p-1 hover:bg-gray-200 rounded"
                                title="Abrir chat"
                              >
                                <ExternalLink className="w-4 h-4 text-gray-500" />
                              </a>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => openAccountModal(franq)}
                              className="p-2 hover:bg-green-50 rounded text-green-600"
                              title="Añadir cuenta"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openEditModal(franq, 'franquiciado')}
                              className="p-2 hover:bg-blue-50 rounded text-blue-600"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(franq.id, 'franquiciado')}
                              className="p-2 hover:bg-red-50 rounded text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Cuentas expandidas */}
                      {isExpanded && (
                        <div className="px-4 pb-4 ml-7">
                          <div className="bg-gray-50 rounded-lg p-3 border">
                            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                              <FileText className="w-4 h-4 mr-2" />
                              Cuentas / Aparatos
                            </h4>
                            {accountsCount === 0 ? (
                              <p className="text-sm text-gray-500 text-center py-2">
                                No hay cuentas registradas
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {franq.accounts.map(account => {
                                  const daysLeft = getDaysUntilExpiry(account.contract_end_date);
                                  return (
                                    <div
                                      key={account.id}
                                      className="flex items-center justify-between bg-white rounded-lg p-3 border"
                                    >
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <Monitor className="w-4 h-4 text-indigo-500" />
                                          <span className="font-medium text-gray-800">{account.name}</span>
                                          {account.taquilla_id && (
                                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                              {account.taquilla_id}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                          {account.city && (
                                            <span className="flex items-center">
                                              <MapPin className="w-3 h-3 mr-1" />
                                              {account.city}
                                            </span>
                                          )}
                                          {account.contract_start_date && (
                                            <span className="flex items-center">
                                              <Calendar className="w-3 h-3 mr-1" />
                                              Inicio: {new Date(account.contract_start_date).toLocaleDateString()}
                                            </span>
                                          )}
                                          {account.contract_end_date && (
                                            <span className={`flex items-center px-2 py-0.5 rounded-full text-xs ${getContractStatusClass(daysLeft)}`}>
                                              {daysLeft !== null && daysLeft < 0 ? (
                                                <>
                                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                                  Expirado hace {Math.abs(daysLeft)} dias
                                                </>
                                              ) : daysLeft !== null ? (
                                                <>
                                                  <Calendar className="w-3 h-3 mr-1" />
                                                  Expira en {daysLeft} dias
                                                </>
                                              ) : null}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <button
                                        onClick={() => handleDeleteAccount(franq, account.id)}
                                        className="p-1 hover:bg-red-50 rounded text-red-500"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <button
                              onClick={() => openAccountModal(franq)}
                              className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition text-sm"
                            >
                              <Plus className="w-4 h-4" />
                              Añadir cuenta
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            // Lugares List
            <div className="divide-y">
              {filteredLugares.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay lugares registrados</p>
                </div>
              ) : (
                filteredLugares.map(lugar => (
                  <div key={lugar.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h3 className="font-semibold text-gray-800">{lugar.name}</h3>
                          <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            {lugar.taquilla_id}
                          </span>
                          <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full capitalize">
                            {lugar.type}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500 mt-1 space-x-4">
                          {lugar.city && (
                            <span className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              {lugar.city}
                            </span>
                          )}
                          {lugar.franquiciado_id && (
                            <span className="text-blue-600">
                              {franquiciados.find(f => f.id === lugar.franquiciado_id)?.name || 'Franquiciado'}
                            </span>
                          )}
                        </div>
                        {/* URL del chat */}
                        <div className="flex items-center mt-2 bg-gray-100 rounded-lg p-2">
                          <MessageSquare className="w-4 h-4 text-green-500 mr-2" />
                          <code className="text-sm text-gray-600 flex-1">{lugar.chat_url}</code>
                          <button
                            onClick={() => handleCopyUrl(lugar.chat_url, lugar.id)}
                            className="ml-2 p-1 hover:bg-gray-200 rounded"
                            title="Copiar URL"
                          >
                            {copiedUrl === lugar.id ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-500" />
                            )}
                          </button>
                          <a
                            href={lugar.chat_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1 p-1 hover:bg-gray-200 rounded"
                            title="Abrir chat"
                          >
                            <ExternalLink className="w-4 h-4 text-gray-500" />
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => openEditModal(lugar, 'lugar')}
                          className="p-2 hover:bg-green-50 rounded text-green-600"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(lugar.id, 'lugar')}
                          className="p-2 hover:bg-red-50 rounded text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingItem ? 'Editar' : 'Nuevo'} {modalType === 'franquiciado' ? 'Franquiciado' : modalType === 'lugar' ? 'Lugar' : 'Cliente final'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>

              {modalType === 'lugar' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ID Taquilla *</label>
                    <input
                      type="text"
                      value={formData.taquilla_id}
                      onChange={(e) => setFormData({ ...formData, taquilla_id: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="TAQ-001"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      {lugarTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Franquiciado</label>
                    <select
                      value={formData.franquiciado_id}
                      onChange={(e) => setFormData({ ...formData, franquiciado_id: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Sin franquiciado</option>
                      {franquiciados.map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {modalType === 'franquiciado' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contacto</label>
                    <input
                      type="text"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Nombre del contacto"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                      <input
                        type="tel"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                </>
              )}

              {modalType === 'cliente' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL del sitio web</label>
                    <input
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="https://ejemplo.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">El sitio donde se incrustara el chat</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Posicion del widget</label>
                    <select
                      value={formData.widget_position}
                      onChange={(e) => setFormData({ ...formData, widget_position: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="bottom-right">Abajo derecha</option>
                      <option value="bottom-left">Abajo izquierda</option>
                      <option value="top-right">Arriba derecha</option>
                      <option value="top-left">Arriba izquierda</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contacto</label>
                    <input
                      type="text"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Nombre del contacto"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                      <input
                        type="tel"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Direccion</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 text-white rounded-lg ${
                    modalType === 'franquiciado' ? 'bg-blue-600 hover:bg-blue-700' :
                    modalType === 'lugar' ? 'bg-green-600 hover:bg-green-700' :
                    'bg-purple-600 hover:bg-purple-700'
                  }`}
                >
                  {editingItem ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para añadir cuenta */}
      {showAccountModal && selectedFranquiciado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                Nueva cuenta para {selectedFranquiciado.name}
              </h3>
              <button onClick={() => setShowAccountModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddAccount} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la cuenta *</label>
                <input
                  type="text"
                  value={accountFormData.name}
                  onChange={(e) => setAccountFormData({ ...accountFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Ej: Museo de Arte Moderno"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ID Taquilla</label>
                  <input
                    type="text"
                    value={accountFormData.taquilla_id}
                    onChange={(e) => setAccountFormData({ ...accountFormData, taquilla_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="10016808"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
                  <input
                    type="text"
                    value={accountFormData.city}
                    onChange={(e) => setAccountFormData({ ...accountFormData, city: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Sevilla"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Fecha firma contrato
                  </label>
                  <input
                    type="date"
                    value={accountFormData.contract_start_date}
                    onChange={(e) => setAccountFormData({ ...accountFormData, contract_start_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Fecha caducidad
                  </label>
                  <input
                    type="date"
                    value={accountFormData.contract_end_date}
                    onChange={(e) => setAccountFormData({ ...accountFormData, contract_end_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowAccountModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Añadir cuenta
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
